import React, { useState, useEffect } from 'react';
import { timeAPI, invoicesAPI, casesAPI, clientsAPI } from '../lib/api';
import { formatCurrency, formatDate, getStatusColor } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { 
  Clock, 
  Plus, 
  Receipt,
  DollarSign,
  Play,
  Pause,
  Edit,
  Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import { FeatureGate } from '../components/common/FeatureGate';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

export const TimeBilling = () => {
  const { hasFeature } = useAuth();
  const [timeEntries, setTimeEntries] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  
  // Persist timer state in localStorage
  const [isTracking, setIsTracking] = useState(() => {
    const saved = localStorage.getItem('time_tracking_active');
    return saved === 'true';
  });
  const [trackingStart, setTrackingStart] = useState(() => {
    const saved = localStorage.getItem('time_tracking_start');
    return saved ? parseInt(saved, 10) : null;
  });
  const [trackingCase, setTrackingCase] = useState(() => {
    return localStorage.getItem('time_tracking_case') || '';
  });
  const [elapsedTime, setElapsedTime] = useState(0);
  
  const [formData, setFormData] = useState({
    case_id: '',
    description: '',
    hours: '',
    hourly_rate: '250',
    date: new Date().toISOString().split('T')[0],
    billable: true,
  });

  useEffect(() => {
    if (hasFeature('time_billing')) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [hasFeature]);

  // Update elapsed time from persisted start time on mount
  useEffect(() => {
    if (isTracking && trackingStart) {
      setElapsedTime(Math.floor((Date.now() - trackingStart) / 1000));
    }
  }, []);

  useEffect(() => {
    let interval;
    if (isTracking && trackingStart) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - trackingStart) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTracking, trackingStart]);

  const fetchData = async () => {
    try {
      const [timeRes, casesRes] = await Promise.all([
        timeAPI.getEntries(),
        casesAPI.getAll()
      ]);
      setTimeEntries(timeRes.data);
      setCases(casesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const entryData = {
        ...formData,
        hours: parseFloat(formData.hours),
        hourly_rate: parseFloat(formData.hourly_rate),
        date: new Date(formData.date).toISOString(),
      };
      
      if (editingEntry) {
        await timeAPI.updateEntry(editingEntry.entry_id, entryData);
        toast.success('Time entry updated');
      } else {
        await timeAPI.createEntry(entryData);
        toast.success('Time entry added');
      }
      fetchData();
      resetForm();
    } catch (error) {
      console.error('Failed to save time entry:', error);
      toast.error('Failed to save time entry');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      case_id: entry.case_id,
      description: entry.description || '',
      hours: entry.hours.toString(),
      hourly_rate: entry.hourly_rate.toString(),
      date: entry.date ? entry.date.split('T')[0] : new Date().toISOString().split('T')[0],
      billable: entry.billable !== false,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) return;
    try {
      await timeAPI.deleteEntry(entryId);
      toast.success('Time entry deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete time entry:', error);
      toast.error('Failed to delete time entry');
    }
  };

  const startTracking = (caseId = '') => {
    const startTime = Date.now();
    setIsTracking(true);
    setTrackingStart(startTime);
    setTrackingCase(caseId);
    setElapsedTime(0);
    // Persist to localStorage
    localStorage.setItem('time_tracking_active', 'true');
    localStorage.setItem('time_tracking_start', startTime.toString());
    localStorage.setItem('time_tracking_case', caseId);
  };

  const stopTracking = () => {
    setIsTracking(false);
    const hours = elapsedTime / 3600;
    setFormData({
      ...formData,
      case_id: trackingCase,
      hours: hours.toFixed(2),
    });
    setIsDialogOpen(true);
    // Clear localStorage
    localStorage.removeItem('time_tracking_active');
    localStorage.removeItem('time_tracking_start');
    localStorage.removeItem('time_tracking_case');
  };

  const resetTracking = () => {
    setIsTracking(false);
    setTrackingStart(null);
    setTrackingCase('');
    setElapsedTime(0);
    localStorage.removeItem('time_tracking_active');
    localStorage.removeItem('time_tracking_start');
    localStorage.removeItem('time_tracking_case');
  };

  const resetForm = () => {
    setFormData({
      case_id: '',
      description: '',
      hours: '',
      hourly_rate: '250',
      date: new Date().toISOString().split('T')[0],
      billable: true,
    });
    setEditingEntry(null);
    setIsDialogOpen(false);
    setTrackingCase('');
    // Also reset tracking state
    resetTracking();
  };

  const getCaseName = (caseId) => {
    const caseItem = cases.find(c => c.case_id === caseId);
    return caseItem?.title || 'Unknown';
  };

  const formatElapsedTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const totalHours = timeEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
  const billableHours = timeEntries.filter(e => e.billable).reduce((sum, e) => sum + (e.hours || 0), 0);
  const totalBillable = timeEntries.filter(e => e.billable).reduce((sum, e) => sum + ((e.hours || 0) * (e.hourly_rate || 0)), 0);

  if (!hasFeature('time_billing')) {
    return (
      <FeatureGate feature="time_billing">
        <div className="h-[600px]" />
      </FeatureGate>
    );
  }

  if (loading) {
    return <PageLoader text="Loading time entries..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="time-billing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Time & Billing</h1>
          <p className="text-slate-500 mt-1">Track billable hours and manage time entries</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-time-entry-btn">
              <Plus className="h-4 w-4" />
              Add Entry
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{editingEntry ? 'Edit Time Entry' : 'New Time Entry'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="case_id">Case *</Label>
                <Select
                  value={formData.case_id}
                  onValueChange={(value) => setFormData({ ...formData, case_id: value })}
                  required
                >
                  <SelectTrigger data-testid="time-case-select">
                    <SelectValue placeholder="Select case" />
                  </SelectTrigger>
                  <SelectContent>
                    {cases.map((caseItem) => (
                      <SelectItem key={caseItem.case_id} value={caseItem.case_id}>
                        {caseItem.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={2}
                  data-testid="time-description-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="hours">Hours *</Label>
                  <Input
                    id="hours"
                    type="number"
                    step="0.25"
                    min="0.25"
                    value={formData.hours}
                    onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                    required
                    data-testid="time-hours-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hourly_rate">Rate ($/hr)</Label>
                  <Input
                    id="hourly_rate"
                    type="number"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit" data-testid="save-time-entry-btn">Save Entry</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Time Tracker */}
      <Card className="bg-gradient-to-r from-slate-900 to-slate-800" data-testid="time-tracker-card">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-full bg-white/10 flex items-center justify-center">
                <Clock className="h-7 w-7 text-white" />
              </div>
              <div>
                <p className="text-white/60 text-sm">Current Timer</p>
                <p className="text-4xl font-bold text-white font-mono">
                  {formatElapsedTime(elapsedTime)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!isTracking && (
                <Select value={trackingCase} onValueChange={setTrackingCase}>
                  <SelectTrigger className="w-[220px] bg-white border-slate-300 text-slate-900">
                    <SelectValue placeholder="Select a case to track" />
                  </SelectTrigger>
                  <SelectContent>
                    {cases.length > 0 ? (
                      cases.map((caseItem) => (
                        <SelectItem key={caseItem.case_id} value={caseItem.case_id}>
                          {caseItem.title}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>No cases available</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              )}
              <Button
                size="lg"
                className={isTracking ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}
                onClick={isTracking ? stopTracking : () => startTracking(trackingCase)}
                disabled={!isTracking && !trackingCase}
                data-testid="timer-btn"
              >
                {isTracking ? (
                  <>
                    <Pause className="h-5 w-5 mr-2" />
                    Stop
                  </>
                ) : (
                  <>
                    <Play className="h-5 w-5 mr-2" />
                    Start
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Hours</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{totalHours.toFixed(1)}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-50">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Billable Hours</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{billableHours.toFixed(1)}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-50">
                <Receipt className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Billable</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{formatCurrency(totalBillable)}</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-50">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Time Entries Table */}
      <Card data-testid="time-entries-table">
        <CardHeader>
          <CardTitle>Recent Time Entries</CardTitle>
        </CardHeader>
        <CardContent>
          {timeEntries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Case</th>
                    <th>Description</th>
                    <th className="text-right">Hours</th>
                    <th className="text-right">Rate</th>
                    <th className="text-right">Amount</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {timeEntries.map((entry) => (
                    <tr key={entry.entry_id}>
                      <td className="text-slate-500">{formatDate(entry.date)}</td>
                      <td className="font-medium">{getCaseName(entry.case_id)}</td>
                      <td className="text-slate-600 max-w-xs truncate">{entry.description}</td>
                      <td className="text-right">{entry.hours.toFixed(2)}</td>
                      <td className="text-right">{formatCurrency(entry.hourly_rate)}</td>
                      <td className="text-right font-medium">
                        {formatCurrency(entry.hours * entry.hourly_rate)}
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0"
                            onClick={() => handleEdit(entry)}
                            data-testid={`edit-entry-${entry.entry_id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(entry.entry_id)}
                            data-testid={`delete-entry-${entry.entry_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Clock className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p>No time entries yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TimeBilling;
